// briefJobStore.js — module-level singleton tracking the in-progress
// Intelligence Brief generation.
//
// Why this exists
// ---------------
// The Intelligence tab unmounts when the user switches to another tab
// (App renders it behind a `{tab === 'intelligence' && ...}` gate). If
// the user kicks off a brief generation and leaves the tab, the state
// held by <Intelligence/> disappears with it: the `generating` phase is
// lost, the cycling copy animation stops, and the completion handler's
// `setPhase('detail')` fires on an unmounted component and is dropped.
// On return, the user sees the list again with no cue that generation
// is still running, or that it just finished.
//
// This store keeps the async job and its outcome in module memory so
// the tab can re-attach on remount. Mirrors the same pub/sub pattern
// used by audioPlayerStore.js.
//
// Lifecycle
// ---------
//   startJob(params, runner) → Promise<Brief>
//     If no job is in flight, installs `active` and kicks runner().
//     If one is already in flight, returns its promise instead of
//     starting a new one (so double-tap / double-mount cannot spawn
//     two simultaneous generations against the rate limit).
//
//   getActive()        → the current job, or null
//   getLastResult()    → { brief, error, finishedAt } from the last
//                        finished job, or null. Lets the remounted tab
//                        auto-navigate into the freshly generated brief.
//   consumeLastResult()→ clears lastResult so we do not re-consume it
//                        on a second mount.
//   subscribe(cb)      → pub/sub for re-renders.

let active = null;
let lastResult = null;

const listeners = new Set();
function emit() {
  for (const cb of listeners) { try { cb(); } catch {} }
}

export function subscribe(cb) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

export function getActive() {
  return active;
}

export function getLastResult() {
  return lastResult;
}

export function consumeLastResult() {
  lastResult = null;
}

// params: { bundleId, customFocus, edition, label }
// runner: async () => Brief
export function startJob(params, runner) {
  if (active) return active.promise;
  const job = {
    bundleId: params.bundleId || null,
    customFocus: params.customFocus || '',
    edition: params.edition || null,
    label: params.label || '',
    startedAt: Date.now(),
    promise: null,
  };
  active = job;
  emit();
  const p = (async () => {
    try {
      const brief = await runner();
      lastResult = {
        bundleId: job.bundleId,
        customFocus: job.customFocus,
        brief,
        error: null,
        finishedAt: Date.now(),
      };
      return brief;
    } catch (e) {
      lastResult = {
        bundleId: job.bundleId,
        customFocus: job.customFocus,
        brief: null,
        error: e,
        finishedAt: Date.now(),
      };
      throw e;
    } finally {
      active = null;
      emit();
    }
  })();
  job.promise = p;
  return p;
}
